import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { TestPackageDTO, TestPackageFilterDTO, TestScenarioDTO, UserDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';

interface ITestScenarioMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const TestScenarioMaintenance: React.FC<ITestScenarioMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [testPackageList, setTestPackageList] = useState<TestPackageDTO[]>([]);
    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [testScenarioDTO, setTestScenarioDTO] = useState<TestScenarioDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestScenarioDTO({
            ...testScenarioDTO,
            id: '',
            name: '',
            testPackageId: parentId ?? '',
            orderExecution: 0,
            description: '',
            executionStatusId: ListId.testingsphere.testpackage.executionstatusstart,
            cycle: 0
        } as TestScenarioDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.testingsphere.testpackage.executionstatus, setExecutionStatusList);
            handleListGetAll(ListId.testingsphere.testpackage.testlevel, setTypeList);
            handleTestPackageGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleTestPackageGetAllAsync = useCallback(async () => {
        const testPackageFilterDTO: TestPackageFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllViewAsync1('testingsphere.testpackage', testPackageFilterDTO, setActionExecuted, setLoading, setTestPackageList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('testingsphere.testscenario', data.id, setLoading, setTestScenarioDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = testScenarioDTO as TestScenarioDTO;
        if (testScenarioDTO?.id === '' || testScenarioDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('testingsphere.testscenario', dto, setTestScenarioDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('testingsphere.testscenario', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [testScenarioDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='TEST SCENARIO DETAILS'
                icon='add_task_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={testScenarioDTO?.name}
                                setValue={setTestScenarioDTO}
                                obj={testScenarioDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='typeId'
                                label={'Test Level'}
                                value={testScenarioDTO?.typeId}
                                setValue={setTestScenarioDTO}
                                obj={testScenarioDTO}
                                valueList={typeList}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='orderExecution'
                                label='Order Execution'
                                value={testScenarioDTO?.orderExecution}
                                setValue={setTestScenarioDTO}
                                obj={testScenarioDTO}
                                required={true}
                                type={'number'}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <SelectField
                                id='testPackageId'
                                label='TestPackage Name'
                                value={testScenarioDTO?.testPackageId}
                                setValue={setTestScenarioDTO}
                                obj={testScenarioDTO}
                                valueList={testPackageList}
                                disabled={parentId ?? testScenarioDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={testScenarioDTO?.description}
                                setValue={setTestScenarioDTO}
                                obj={testScenarioDTO}
                                required={true}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'executionStatusId'}
                                label='Status Execution'
                                value={(<ChipStatus label='Execution Status' value={String(executionStatusList.find(x => x.id === testScenarioDTO?.executionStatusId)?.value || 'No Run')} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'cycle'}
                                label='Cycle'
                                value={(<ChipStatus label='Cycle' value={String(testScenarioDTO?.cycle || 0)} />)}
                                hasDivider={false}
                            />
                        </Grid>
                        {testScenarioDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'startExecutionDateTime'}
                                        label='Start Execution'
                                        value={formatDateTime(String(testScenarioDTO.startExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'endExecutionDateTime'}
                                        label='End Execution'
                                        value={formatDateTime(String(testScenarioDTO.endExecutionDateTime))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'executionTime'}
                                        label='Execution Time'
                                        value={testScenarioDTO.startExecutionDateTime ? dateDiff(String(testScenarioDTO.startExecutionDateTime), String(testScenarioDTO.endExecutionDateTime)) : undefined}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={testScenarioDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === testScenarioDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(testScenarioDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === testScenarioDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(testScenarioDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};